<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import type { YieldHistory } from '@/types/farmsales'
import yieldsHistoryService from '@/api/yieldshistory'
import WelcomeBanner from '@/components/farm/supervisor/WelcomeBanner.vue'
import { jsPDF } from "jspdf"
import autoTable from "jspdf-autotable"
import { addPdfWatermark } from '@/utils/pdfWatermark'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notification'

const authStore = useAuthStore()
const notificationStore = useNotificationStore()
const history = ref<YieldHistory[]>([])

// Search and filters
const searchQuery = ref("")
const createdByFilter = ref("")
const createdByOptions = ref<string[]>([])
const farmOptions = ref<string[]>([])
const selectedFarmFilter = ref("")
const cropOptions = ref<string[]>([])
const selectedCropFilter = ref("")

// Pagination
const currentPage = ref(1)
const itemsPerPage = ref(10)

const fetchHistory = async () => {
  history.value = await yieldsHistoryService.list()

  // Extract unique created_by names for filter
  const createdBySet = new Set(history.value.map(h => h.created_by_name))
  createdByOptions.value = Array.from(createdBySet).filter(Boolean) as string[]

  // Extract unique farm names for filter
  const farmSet = new Set(history.value.map(h => h.farm_name))
  farmOptions.value = Array.from(farmSet).filter(Boolean) as string[]

  // Extract unique crop names for filter
  const cropSet = new Set(history.value.map(h => h.yield_label))
  cropOptions.value = Array.from(cropSet).filter(Boolean) as string[]
}

onMounted(() => {
  fetchHistory()
})

// Filtered history
const filteredHistory = computed(() => {
  return history.value.filter(record => {
    const matchesSearch = searchQuery.value === "" ||
      record.farm_name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      record.yield_label.toLowerCase().includes(searchQuery.value.toLowerCase())

    const matchesCreator = createdByFilter.value === "" ||
      record.created_by_name === createdByFilter.value

    const matchesFarm = selectedFarmFilter.value === "" ||
      record.farm_name === selectedFarmFilter.value

    const matchesCrop = selectedCropFilter.value === "" ||
      record.yield_label === selectedCropFilter.value

    return matchesSearch && matchesCreator && matchesFarm && matchesCrop
  })
})

// Calculate totals for filtered history - only change column
const changeTotal = computed(() => {
  if (!selectedFarmFilter.value || !selectedCropFilter.value) {
    return 0
  }

  return filteredHistory.value.reduce((total, record) => {
    return total + (parseFloat(record.change.toString()) || 0)
  }, 0)
})

// Paginated history
const paginatedHistory = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredHistory.value.slice(start, end)
})

// Total pages
const totalPages = computed(() => {
  return Math.ceil(filteredHistory.value.length / itemsPerPage.value)
})

// Format numbers with commas
const formatNumber = (num: number | string): string => {
  const numValue = typeof num === 'string' ? parseFloat(num) : num
  return isNaN(numValue) ? '0' : numValue.toLocaleString('en-US')
}

// Generate PDF for selected farm and crop
const generatePDF = () => {
  if (!selectedFarmFilter.value || !selectedCropFilter.value) {
    notificationStore.showWarning('Please select both a farm and a crop first.')
    return
  }

  const filteredRecords = filteredHistory.value.filter(
    h => h.farm_name === selectedFarmFilter.value && h.yield_label === selectedCropFilter.value
  )

  if (filteredRecords.length === 0) {
    notificationStore.showWarning('No records found for the selected farm and crop.')
    return
  }

  // Calculate totals for the selected farm and crop
  const pdfTotals = filteredRecords.reduce((acc, record) => {
    return {
      change: acc.change + (parseFloat(record.change.toString()) || 0),
      before: acc.before + (parseFloat(record.before_quantity.toString()) || 0),
      after: acc.after + (parseFloat(record.after_quantity.toString()) || 0)
    }
  }, { change: 0, before: 0, after: 0 })

  // Create new PDF document
  const doc = new jsPDF()

  // Set font
  doc.setFont("helvetica")

  // Add title
  doc.setFontSize(20)
  doc.setTextColor(40, 103, 50)
  doc.text("Yield History Report", 105, 20, { align: "center" })

  // Add farm and crop details
  doc.setFontSize(14)
  doc.setTextColor(0, 0, 0)
  doc.text(`Farm: ${selectedFarmFilter.value}`, 20, 35)
  doc.text(`Crop: ${selectedCropFilter.value}`, 20, 45)
  doc.text(`Total Records: ${filteredRecords.length}`, 20, 55)

  // Prepare table data
  const tableData = filteredRecords.map((record, index) => [
    (index + 1).toString(),
    record.year.toString(),
    formatNumber(record.change),
    formatNumber(record.before_quantity),
    formatNumber(record.after_quantity),
    record.note || 'N/A',
    record.created_by_name,
    new Date(record.created_at).toLocaleDateString()
  ])

  // Add history table
  doc.setFontSize(12)
  autoTable(doc, {
    startY: 65,
    head: [['#', 'Year', 'Change', 'Before', 'After', 'Note', 'Created By', 'Date']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [76, 175, 80],
      textColor: [0,0,255],
      fontStyle: 'bold'
    },
    styles: {
      fontSize: 10,
      cellPadding: 3,
      overflow: 'linebreak'
    },
    margin: { top: 65 }
  })

  // Add totals section
  interface ExtendedJSPDF extends jsPDF {
    lastAutoTable: {
      finalY: number;
    };
  }
  const finalY = (doc as ExtendedJSPDF).lastAutoTable.finalY + 10
  doc.setFontSize(12)
  doc.setFont('bold')
  doc.text('TOTALS:', 20, finalY)
  doc.setFont('normal')
  doc.text(`Change: ${formatNumber(pdfTotals.change)}`, 20, finalY + 10)

  // Add footer with page numbers
  const pageCount = doc.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFontSize(10)
    doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 10, {
      align: 'center'
    })
  }

  // Save the PDF
  addPdfWatermark(doc)
  const fileName = `Yield_History_${selectedFarmFilter.value}_${selectedCropFilter.value}_${new Date().toISOString().slice(0, 10)}.pdf`
  doc.save(fileName)
}

// Reset filters
const resetFilters = () => {
  searchQuery.value = ""
  createdByFilter.value = ""
  selectedFarmFilter.value = ""
  selectedCropFilter.value = ""
  currentPage.value = 1
}
</script>

<template>
  <div
    class="min-h-screen font-sans bg-gray-50 p-4 transition-all duration-300"
  >
    <WelcomeBanner />

    <div class="bg-white rounded-lg shadow-md p-4 md:p-6 mt-4 md:mt-6">
      <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h1 class="text-xl md:text-2xl font-bold text-gray-800">Yield History</h1>
      </div>

      <!-- Search and Filter Section -->
      <div class="bg-gray-100 p-4 rounded-lg mb-6">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <!-- Search Input -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div class="relative">
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search farm or crop..."
                class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400 absolute left-3 top-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          <!-- Created By Filter -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Filter by Created By</label>
            <select
              v-model="createdByFilter"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="">All Users</option>
              <option v-for="user in createdByOptions" :key="user" :value="user">{{ user }}</option>
            </select>
          </div>

          <!-- Farm Filter -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Filter by Farm</label>
            <select
              v-model="selectedFarmFilter"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="">All Farms</option>
              <option v-for="farm in farmOptions" :key="farm" :value="farm">{{ farm }}</option>
            </select>
          </div>

          <!-- Crop Filter -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Filter by Crop</label>
            <select
              v-model="selectedCropFilter"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="">All Crops</option>
              <option v-for="crop in cropOptions" :key="crop" :value="crop">{{ crop }}</option>
            </select>
          </div>

          <!-- Action Buttons -->
          <div class="md:col-span-4 flex flex-col md:flex-row md:items-end space-y-2 md:space-y-0 md:space-x-2">
            <button
              @click="resetFilters"
              class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center justify-center transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reset
            </button>

            <button
              @click="generatePDF"
              :disabled="!selectedFarmFilter || !selectedCropFilter"
              class="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
              title="Download yield history for selected farm and crop"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span class="hidden md:inline">Download PDF Report</span>
              <span class="md:hidden">PDF Report</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Info Banner when farm and crop are selected -->
      <div v-if="selectedFarmFilter && selectedCropFilter" class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span class="text-blue-700 text-sm md:text-base">
          Showing yield history for <strong>{{ selectedFarmFilter }}</strong> - <strong>{{ selectedCropFilter }}</strong>.
          Use the "Download PDF Report" button to generate a detailed report.
        </span>
      </div>

      <!-- Table Container with horizontal scrolling -->
      <div class="overflow-x-auto rounded-lg shadow">
        <table class="min-w-full bg-white border border-gray-200">
          <thead class="bg-green-700 text-white">
            <tr>
              <th class="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium uppercase tracking-wider">#</th>
              <th class="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium uppercase tracking-wider">Farm Name</th>
              <th class="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium uppercase tracking-wider">Crop & Harvest</th>
              <th class="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium uppercase tracking-wider">Year</th>
              <th class="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium uppercase tracking-wider">Change</th>
              <th class="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium uppercase tracking-wider">Before</th>
              <th class="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium uppercase tracking-wider">After</th>
              <th class="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium uppercase tracking-wider">Note</th>
              <th class="hidden md:table-cell px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Created By</th>
              <th class="hidden lg:table-cell px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Owner</th>
              <th class="hidden xl:table-cell px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Created At</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-for="(h, i) in paginatedHistory" :key="h.id" class="hover:bg-gray-50">
              <td class="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap">{{ (currentPage - 1) * itemsPerPage + i + 1 }}</td>
              <td class="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap">{{ h.farm_name }}</td>
              <td class="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap">{{ h.yield_label }}</td>
              <td class="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap">{{ h.year }}</td>
              <td class="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap">{{ formatNumber(h.change) }}</td>
              <td class="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap">{{ formatNumber(h.before_quantity) }}</td>
              <td class="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap">{{ formatNumber(h.after_quantity) }}</td>
              <td class="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap max-w-xs truncate">{{ h.note || 'N/A' }}</td>
              <td class="hidden md:table-cell px-4 py-3 whitespace-nowrap">{{ h.created_by_name }}</td>
              <td class="hidden lg:table-cell px-4 py-3 whitespace-nowrap">{{ h.owner_name }}</td>
              <td class="hidden xl:table-cell px-4 py-3 whitespace-nowrap">{{ new Date(h.created_at).toLocaleString() }}</td>
            </tr>

            <!-- Total Row - Only show when both farm and crop are selected -->
            <tr v-if="selectedFarmFilter && selectedCropFilter && filteredHistory.length > 0" class="bg-gray-50 font-semibold">
              <td colspan="4" class="px-4 py-3 text-right">Total Change:</td>
              <td class="px-4 py-3 whitespace-nowrap">{{ formatNumber(changeTotal) }}</td>
              <td colspan="6"></td>
            </tr>

            <tr v-if="paginatedHistory.length === 0">
              <td colspan="11" class="px-4 py-6 text-center text-gray-500">
                No yield history records found matching your criteria.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="flex flex-col md:flex-row items-center justify-between mt-6 px-2 space-y-4 md:space-y-0">
        <div class="text-sm text-gray-700">
          Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
          {{ Math.min(currentPage * itemsPerPage, filteredHistory.length) }} of
          {{ filteredHistory.length }} results
        </div>

        <div class="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2">
          <select
            v-model="itemsPerPage"
            class="px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option :value="5">5 per page</option>
            <option :value="10">10 per page</option>
            <option :value="20">20 per page</option>
            <option :value="50">50 per page</option>
          </select>

          <div class="flex space-x-2">
            <button
              @click="currentPage--"
              :disabled="currentPage === 1"
              class="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
            >
              Previous
            </button>

            <div class="flex space-x-1 overflow-x-auto">
              <button
                v-for="page in totalPages"
                :key="page"
                @click="currentPage = page"
                :class="[
                  'px-3 py-1.5 border rounded-md transition-colors min-w-[2.5rem]',
                  currentPage === page
                    ? 'bg-green-600 text-white border-green-600'
                    : 'border-gray-300 hover:bg-gray-100'
                ]"
              >
                {{ page }}
              </button>
            </div>

            <button
              @click="currentPage++"
              :disabled="currentPage === totalPages"
              class="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.font-sans {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

table {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

th {
  font-weight: 600;
}

td {
  font-weight: 400;
}

@media (max-width: 768px) {
  .overflow-x-auto {
    -webkit-overflow-scrolling: touch;
  }
}
</style>
